import { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'
import {
  isNonEmptyString,
  isSafeEmailHint,
  isSafeInternalRedirectPath,
  readSingleQueryParameterValue
} from '@/services/RedirectTargetValidation'

/**
 * Clé unique de persistance. Toute la mémoire de redirection de l'authentification
 * vit ici, et nulle part ailleurs : un seul emplacement à lire, à purger et à invalider.
 */
const STORAGE_KEY = 'authentication_redirect_context'

/**
 * Version du format stocké. Un format inconnu est jeté plutôt que deviné.
 */
const STORAGE_FORMAT_VERSION = 1

/**
 * Durée de vie d'un contexte de redirection : une heure.
 *
 * Couvre un aller-retour par e-mail lent sans ressusciter le flux de la veille.
 * Non rafraîchie en lecture : c'est la date de capture qui compte.
 */
export const AUTHENTICATION_REDIRECT_CONTEXT_TIME_TO_LIVE_IN_MILLISECONDS = 60 * 60 * 1000

/**
 * Durée de vie du handshake avec le fournisseur d'identité externe (state + PKCE) :
 * dix minutes, le temps d'un aller-retour vers ADFS / Entra ID.
 */
export const IDENTITY_PROVIDER_HANDSHAKE_TIME_TO_LIVE_IN_MILLISECONDS = 10 * 60 * 1000

/**
 * Clé du contexte utilisé quand la requête d'autorisation ne nomme aucun service.
 */
const DIRECT_CONTEXT_KEY = 'direct'

/**
 * Préfixe des clés de contexte portant un service nommé.
 */
const SERVICE_CONTEXT_KEY_PREFIX = 'service:'

/**
 * Préfixe des messages de journal, pour les retrouver dans une console.
 */
const LOG_PREFIX = '[AuthenticationRedirectContext]'

/**
 * Valeur de `response_type` posée par défaut quand l'application appelante ne l'a pas
 * transmise.
 *
 * Depuis la mise en conformité OIDC, `/authentication/v1.1/oauth/authorize` exige
 * `response_type` et répond `invalid_request` en son absence. Toutes les applications
 * appelantes ne le transmettent pas — seul le portail staff le fait aujourd'hui, une
 * trentaine d'autres clients enregistrés ne sont pas vérifiables depuis ce dépôt — et
 * une omission a déjà cassé la connexion en staging sur un autre paramètre du même
 * mécanisme. `code` est la seule valeur que le document de découverte annonce
 * (`response_types_supported: ["code"]`) : la poser par défaut ne change le
 * comportement d'aucun client existant, et évite une nouvelle panne le temps que tous
 * les clients migrent.
 */
const DEFAULT_AUTHORIZATION_RESPONSE_TYPE = 'code'

/* eslint-disable camelcase */
/**
 * Requête d'autorisation OAuth telle que l'application appelante l'a émise.
 *
 * Les champs gardent leur nom filaire : ce ne sont pas nos identifiants, et une table
 * de correspondance serait du bruit au moment de reconstruire les `URLSearchParams`.
 */
export interface OAuthAuthorizationRequest {
  client_id?: string
  redirect_uri?: string
  return_to?: string
  state?: string
  service?: string
  code_challenge?: string
  response_type?: string
  scope?: string
  nonce?: string
  code_challenge_method?: string
  // `prompt` et `max_age` sont transportés dès maintenant mais pas encore traités par le
  // serveur (lot 3 du plan OIDC) : un paramètre inconnu y est ignoré sans risque, ce qui
  // évite une seconde modification de cette SPA le jour où ils seront pris en compte.
  prompt?: string
  max_age?: string
}
/* eslint-enable camelcase */

/**
 * Liaison en cours avec le fournisseur d'identité externe.
 *
 * Vit dans le contexte persistant, et non plus dans `sessionStorage` : un retour de
 * fournisseur qui atterrit dans un autre onglet doit pouvoir être vérifié.
 */
export interface IdentityProviderHandshake {
  state: string
  codeVerifier: string
  createdAtEpochMilliseconds: number
}

/**
 * Tout ce qu'il faut savoir pour ramener l'utilisateur là où il allait.
 */
export interface AuthenticationRedirectContext {
  createdAtEpochMilliseconds: number
  expiresAtEpochMilliseconds: number
  oauthAuthorizationRequest: OAuthAuthorizationRequest
  internalRedirectPath: string | null
  emailAddress: string | null
  shouldAutomaticallySubmitEmail: boolean
  identityProviderHandshake: IdentityProviderHandshake | null
}

interface StoredContexts {
  version: number
  activeContextKey: string | null
  contextsByKey: Record<string, AuthenticationRedirectContext>
}

/**
 * Paramètres de la requête d'autorisation reconnus dans une query string.
 *
 * `state` et `code_challenge` sont volontairement absents des déclencheurs de capture
 * (cf. `looksLikeAuthorizationRequest`) : l'URL de retour du fournisseur d'identité
 * porte un `state`, et le laisser déclencher une capture écrasait le contexte de
 * l'application appelante.
 */
/* eslint-disable camelcase */
const AUTHORIZATION_REQUEST_PARAMETER_NAMES: Array<keyof OAuthAuthorizationRequest> = [
  'client_id',
  'redirect_uri',
  'return_to',
  'state',
  'service',
  'code_challenge',
  'response_type',
  'scope',
  'nonce',
  'code_challenge_method',
  'prompt',
  'max_age'
]

/**
 * Noms sous lesquels une application appelante transmet l'adresse e-mail de la personne
 * à connecter, par ordre de priorité : `login_hint` est le nom standard OIDC, `email`
 * l'alias historique des liens générés en interne.
 *
 * Liste séparée de `AUTHORIZATION_REQUEST_PARAMETER_NAMES`, et ce n'est pas un détail :
 * cette dernière est rejouée telle quelle vers le point d'entrée d'autorisation de l'API
 * (cf. `buildOAuthAuthorizeQueryParameters`), qui n'attend pas de hint. L'adresse est un
 * confort d'affichage, elle ne fait pas partie de la requête d'autorisation.
 */
const EMAIL_HINT_PARAMETER_NAMES = ['login_hint', 'email']

/**
 * Paramètres dont la seule présence signale une requête d'autorisation entrante.
 */
const AUTHORIZATION_REQUEST_TRIGGER_PARAMETER_NAMES: Array<keyof OAuthAuthorizationRequest> = [
  'client_id',
  'service',
  'return_to'
]
/* eslint-enable camelcase */

/**
 * Construit la clé de contexte correspondant à un service.
 */
export function buildServiceContextKey (service: string): string {
  return `${SERVICE_CONTEXT_KEY_PREFIX}${service}`
}

/**
 * Vrai si la route entrante est une requête d'autorisation émise par une application
 * appelante, et non le retour d'un fournisseur d'identité.
 *
 * Deux garde-fous, volontairement redondants : la route de rappel est marquée
 * `meta.isIdentityProviderCallback`, et seuls `client_id`, `service` ou `return_to`
 * déclenchent une capture. Un `state` seul — ce que porte l'URL de rappel — n'en
 * déclenche jamais, même si le marqueur de route venait à être oublié.
 */
export function looksLikeAuthorizationRequest (route: RouteLocationNormalized): boolean {
  if (route.meta.isIdentityProviderCallback === true) {
    return false
  }

  return AUTHORIZATION_REQUEST_TRIGGER_PARAMETER_NAMES.some(
    (parameterName) => readSingleQueryParameterValue(route.query[parameterName]) !== null
  )
}

/**
 * Capture le contexte de redirection porté par la query string d'une route.
 *
 * Sans effet si la route n'est pas une requête d'autorisation : c'est ce qui empêche
 * le retour du fournisseur d'identité de réécrire le contexte.
 */
export function captureAuthenticationRedirectContextFromRoute (
  route: RouteLocationNormalized
): AuthenticationRedirectContext | null {
  if (!looksLikeAuthorizationRequest(route)) {
    return null
  }

  const oauthAuthorizationRequest: OAuthAuthorizationRequest = {}
  AUTHORIZATION_REQUEST_PARAMETER_NAMES.forEach((parameterName) => {
    const parameterValue = readSingleQueryParameterValue(route.query[parameterName])
    if (parameterValue !== null) {
      oauthAuthorizationRequest[parameterName] = parameterValue
    }
  })

  const emailHint = readEmailHintFromRoute(route)

  const service = oauthAuthorizationRequest.service
  const contextKey = isNonEmptyString(service)
    ? buildServiceContextKey(service as string)
    : DIRECT_CONTEXT_KEY

  const freshContext = buildEmptyContext()

  return writeContext(contextKey, (existingContext) => ({
    ...freshContext,
    ...existingContext,
    // La requête d'autorisation entrante REMPLACE la précédente, elle ne la complète
    // pas : c'est un tout. Fusionner laissait le `state`, le `return_to`, le
    // `redirect_uri` ou le `code_challenge` d'une visite antérieure garnir les
    // paramètres absents de la nouvelle — et un `state` périmé rejoué fait échouer la
    // vérification côté application appelante, qui renvoie l'utilisateur ici.
    oauthAuthorizationRequest: oauthAuthorizationRequest,
    // Fenêtre de vie neuve, réimposée après les étalements : la clé de contexte étant
    // dérivée du service, une requête neuve reprenait sinon l'expiration de la visite
    // précédente au même service. Un contexte hérité d'il y a 55 minutes expirait donc
    // en pleine connexion — saisie du mot de passe, aller-retour vers le fournisseur
    // d'identité, lien reçu par e-mail — et l'utilisateur perdait sa destination.
    createdAtEpochMilliseconds: freshContext.createdAtEpochMilliseconds,
    expiresAtEpochMilliseconds: freshContext.expiresAtEpochMilliseconds,
    // Après les fusions : une requête d'autorisation fraîche porte l'adresse de la
    // personne que l'application appelante attend, et prime donc sur celle qu'une
    // navigation interne antérieure aurait mémorisée. Sans hint exploitable, on ne
    // touche pas à l'existant — un contexte ne se vide jamais par omission.
    emailAddress: emailHint ?? existingContext?.emailAddress ?? null,
    shouldAutomaticallySubmitEmail: emailHint !== null
      ? true
      : existingContext?.shouldAutomaticallySubmitEmail ?? false
  }))
}

/**
 * Lit l'adresse e-mail transmise par l'application appelante, ou `null`.
 *
 * Premier nom exploitable retenu, dans l'ordre de `EMAIL_HINT_PARAMETER_NAMES` : un
 * client qui envoie les deux voit son `login_hint` l'emporter. Une valeur qui n'a pas
 * la forme d'une adresse est traitée comme absente — elle vient d'une URL publique,
 * et garnir le champ avec n'importe quoi vaut moins qu'un champ vide.
 */
function readEmailHintFromRoute (route: RouteLocationNormalized): string | null {
  for (const parameterName of EMAIL_HINT_PARAMETER_NAMES) {
    const parameterValue = readSingleQueryParameterValue(route.query[parameterName])
    if (parameterValue !== null && isSafeEmailHint(parameterValue)) {
      return parameterValue.trim()
    }
  }

  return null
}

/**
 * Renvoie le contexte actif, après purge des contextes expirés.
 */
export function readActiveAuthenticationRedirectContext (): AuthenticationRedirectContext | null {
  const storedContexts = readStoredContexts()
  if (storedContexts === null || storedContexts.activeContextKey === null) {
    return null
  }

  return storedContexts.contextsByKey[storedContexts.activeContextKey] ?? null
}

/**
 * Renvoie la clé du contexte actif, après purge des contextes expirés.
 */
export function readActiveContextKey (): string | null {
  const storedContexts = readStoredContexts()
  return storedContexts === null ? null : storedContexts.activeContextKey
}

/**
 * Renvoie le contexte d'une clé donnée, après purge des contextes expirés.
 */
export function readAuthenticationRedirectContextByKey (
  contextKey: string
): AuthenticationRedirectContext | null {
  const storedContexts = readStoredContexts()
  return storedContexts === null ? null : storedContexts.contextsByKey[contextKey] ?? null
}

/**
 * Vrai si le contexte porte de quoi demander un code d'autorisation.
 *
 * `client_id` et rien d'autre : sans client identifié, le point d'entrée d'autorisation
 * n'a pas d'URI de retour à laquelle remettre un code. Un contexte qui ne nomme qu'un
 * `service` n'est donc pas une requête d'autorisation — c'est une destination connue,
 * traitée par `readRequestedServiceId` : on envoie l'utilisateur sur ce service, et
 * c'est l'application qui émet ensuite sa requête d'autorisation en règle.
 */
export function hasUsableOAuthAuthorizationRequest (
  context: AuthenticationRedirectContext | null
): boolean {
  if (context === null) {
    return false
  }

  return isNonEmptyString(context.oauthAuthorizationRequest.client_id)
}

/**
 * Identifiant du service nommé par le contexte, ou `null`.
 *
 * Renseigné dès qu'une URL entrante porte `service=<id>`, que la requête soit une
 * requête d'autorisation complète ou le seul nom du service. C'est la destination
 * connue de l'utilisateur : la connaître dispense de la lui demander.
 */
export function readRequestedServiceId (
  context: AuthenticationRedirectContext | null
): string | null {
  const service = context?.oauthAuthorizationRequest.service
  return isNonEmptyString(service) ? (service as string) : null
}

/**
 * Mémorise la page interne que l'utilisateur voulait atteindre avant d'être renvoyé
 * vers la connexion. Une valeur non conforme est ignorée, jamais stockée.
 */
export function saveInternalRedirectPath (candidatePath: string): void {
  if (!isSafeInternalRedirectPath(candidatePath)) {
    console.warn(`${LOG_PREFIX} chemin de redirection interne refusé`, candidatePath)
    return
  }

  const contextKey = readActiveContextKey() ?? DIRECT_CONTEXT_KEY
  writeContext(contextKey, (existingContext) => ({
    ...buildEmptyContext(),
    ...existingContext,
    internalRedirectPath: candidatePath
  }))
}

/**
 * Mémorise l'adresse e-mail saisie, pour la retrouver après un détour par les écrans
 * de mot de passe oublié, de première connexion ou de lien de connexion — sans la
 * faire voyager dans la query string.
 */
export function saveEmailAddress (
  emailAddress: string,
  shouldAutomaticallySubmitEmail: boolean
): void {
  if (!isNonEmptyString(emailAddress)) {
    return
  }

  const contextKey = readActiveContextKey() ?? DIRECT_CONTEXT_KEY
  writeContext(contextKey, (existingContext) => ({
    ...buildEmptyContext(),
    ...existingContext,
    emailAddress: emailAddress,
    shouldAutomaticallySubmitEmail: shouldAutomaticallySubmitEmail
  }))
}

/**
 * Mémorise la liaison en cours avec le fournisseur d'identité externe.
 */
export function saveIdentityProviderHandshake (state: string, codeVerifier: string): void {
  const contextKey = readActiveContextKey() ?? DIRECT_CONTEXT_KEY
  writeContext(contextKey, (existingContext) => ({
    ...buildEmptyContext(),
    ...existingContext,
    identityProviderHandshake: {
      state: state,
      codeVerifier: codeVerifier,
      createdAtEpochMilliseconds: Date.now()
    }
  }))
}

/**
 * Renvoie la liaison en cours avec le fournisseur d'identité externe, si elle n'a pas
 * expiré. Cherchée dans tous les contextes : le retour du fournisseur n'indique pas
 * quel contexte l'a lancée.
 */
export function readIdentityProviderHandshake (): IdentityProviderHandshake | null {
  const storedContexts = readStoredContexts()
  if (storedContexts === null) {
    return null
  }

  const expirationLimit = Date.now() - IDENTITY_PROVIDER_HANDSHAKE_TIME_TO_LIVE_IN_MILLISECONDS
  const contexts = Object.values(storedContexts.contextsByKey)
  const contextWithHandshake = contexts.find((currentContext) => {
    const handshake = currentContext.identityProviderHandshake
    return handshake !== null && handshake.createdAtEpochMilliseconds >= expirationLimit
  })

  return contextWithHandshake?.identityProviderHandshake ?? null
}

/**
 * Efface la liaison avec le fournisseur d'identité externe dans tous les contextes :
 * elle est à usage unique.
 */
export function clearIdentityProviderHandshake (): void {
  const storedContexts = readStoredContexts()
  if (storedContexts === null) {
    return
  }

  const contextsByKey: Record<string, AuthenticationRedirectContext> = {}
  Object.entries(storedContexts.contextsByKey).forEach(([contextKey, context]) => {
    contextsByKey[contextKey] = { ...context, identityProviderHandshake: null }
  })

  writeStoredContexts({ ...storedContexts, contextsByKey: contextsByKey })
}

/**
 * Construit les paramètres de l'appel au point d'entrée d'autorisation de l'API.
 */
export function buildOAuthAuthorizeQueryParameters (
  context: AuthenticationRedirectContext
): URLSearchParams {
  const queryParameters = new URLSearchParams()
  AUTHORIZATION_REQUEST_PARAMETER_NAMES.forEach((parameterName) => {
    const parameterValue = context.oauthAuthorizationRequest[parameterName]
    if (isNonEmptyString(parameterValue)) {
      queryParameters.append(parameterName, parameterValue as string)
    }
  })

  // Défaut posé ici, après recopie telle quelle des paramètres transmis : une
  // application appelante qui envoie déjà `response_type` garde sa valeur intacte,
  // seule l'absence est comblée (cf. `DEFAULT_AUTHORIZATION_RESPONSE_TYPE`).
  if (!isNonEmptyString(context.oauthAuthorizationRequest.response_type)) {
    queryParameters.append('response_type', DEFAULT_AUTHORIZATION_RESPONSE_TYPE)
  }

  return queryParameters
}

/**
 * Seul endroit qui décide où va l'utilisateur après une authentification réussie.
 *
 * Précédence : la redirection OAuth d'abord — c'est une application qui attend son
 * utilisateur ; puis la page interne demandée ; puis la route du sélecteur de service.
 *
 * Cette dernière ne signifie pas « affiche une liste » : `ServicesView` y décide entre
 * le service nommé par le contexte, le service unique de l'utilisateur, et — seulement
 * s'il reste un choix à faire — la liste.
 */
export function resolvePostAuthenticationTarget (
  context: AuthenticationRedirectContext | null
): RouteLocationRaw {
  if (hasUsableOAuthAuthorizationRequest(context)) {
    return { name: 'oauth-redirect' }
  }

  const internalRedirectPath = context?.internalRedirectPath
  if (isNonEmptyString(internalRedirectPath) &&
      isSafeInternalRedirectPath(internalRedirectPath as string)) {
    // Chaîne, pas `{ path }` : vue-router 4 ne garde que la partie chemin d'un `path`
    // d'objet et jette la query string. La page mémorisée est un `fullPath`
    // (`/connect-as?userId=…&service=…&exit_to=…`) : sous forme d'objet, la connexion
    // ramenait sur `/connect-as` sans aucun paramètre (« Paramètres manquants »).
    return internalRedirectPath as string
  }

  return { name: 'services' }
}

/**
 * Supprime le contexte actif, en laissant les autres intacts.
 */
export function clearActiveAuthenticationRedirectContext (): void {
  const activeContextKey = readActiveContextKey()
  if (activeContextKey === null) {
    return
  }

  clearAuthenticationRedirectContextByKey(activeContextKey)
}

/**
 * Supprime un contexte nommé, en laissant les autres intacts.
 */
export function clearAuthenticationRedirectContextByKey (contextKey: string): void {
  const storedContexts = readStoredContexts()
  if (storedContexts === null) {
    return
  }

  const { [contextKey]: removedContext, ...remainingContexts } = storedContexts.contextsByKey
  if (removedContext === undefined) {
    return
  }

  writeStoredContexts({
    ...storedContexts,
    activeContextKey: storedContexts.activeContextKey === contextKey
      ? null
      : storedContexts.activeContextKey,
    contextsByKey: remainingContexts
  })
}

/**
 * Supprime tous les contextes. Appelé à la déconnexion : un contexte survivant
 * enverrait l'utilisateur suivant vers l'application du précédent.
 */
export function clearAllAuthenticationRedirectContexts (): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (error) {
    console.warn(`${LOG_PREFIX} suppression impossible`, error)
  }
}

/**
 * Adopte les emplacements de l'ancienne implémentation, une seule fois.
 *
 * Sans cette reprise, tout utilisateur en cours de flux au moment du déploiement perd
 * sa redirection.
 */
export function migrateLegacyOAuthStorageEntries (): void {
  try {
    if (localStorage.getItem(STORAGE_KEY) !== null) {
      removeLegacyStorageEntries()
      return
    }

    const legacyKeys = collectLegacyOAuthKeys()
    const contextsByKey: Record<string, AuthenticationRedirectContext> = {}
    let activeContextKey: string | null = null

    legacyKeys.forEach((legacyKey) => {
      const legacyContext = parseLegacyOAuthEntry(legacyKey)
      if (legacyContext === null) {
        return
      }

      const service = legacyContext.oauthAuthorizationRequest.service
      const contextKey = isNonEmptyString(service)
        ? buildServiceContextKey(service as string)
        : DIRECT_CONTEXT_KEY
      contextsByKey[contextKey] = legacyContext
      activeContextKey = contextKey
    })

    if (activeContextKey !== null) {
      writeStoredContexts({
        version: STORAGE_FORMAT_VERSION,
        activeContextKey: activeContextKey,
        contextsByKey: contextsByKey
      })
    }

    removeLegacyStorageEntries()
  } catch (error) {
    console.warn(`${LOG_PREFIX} reprise des anciennes clés impossible`, error)
  }
}

/**
 * Contexte vierge, base de tout écriture.
 */
function buildEmptyContext (): AuthenticationRedirectContext {
  const createdAtEpochMilliseconds = Date.now()
  return {
    createdAtEpochMilliseconds: createdAtEpochMilliseconds,
    expiresAtEpochMilliseconds:
      createdAtEpochMilliseconds + AUTHENTICATION_REDIRECT_CONTEXT_TIME_TO_LIVE_IN_MILLISECONDS,
    oauthAuthorizationRequest: {},
    internalRedirectPath: null,
    emailAddress: null,
    shouldAutomaticallySubmitEmail: false,
    identityProviderHandshake: null
  }
}

/**
 * Applique une transformation à un contexte et le rend actif.
 */
function writeContext (
  contextKey: string,
  buildNextContext: (
    existingContext: AuthenticationRedirectContext | undefined
  ) => AuthenticationRedirectContext
): AuthenticationRedirectContext {
  const storedContexts = readStoredContexts() ?? {
    version: STORAGE_FORMAT_VERSION,
    activeContextKey: null,
    contextsByKey: {}
  }

  const nextContext = buildNextContext(storedContexts.contextsByKey[contextKey])
  writeStoredContexts({
    ...storedContexts,
    activeContextKey: contextKey,
    contextsByKey: { ...storedContexts.contextsByKey, [contextKey]: nextContext }
  })

  return nextContext
}

/**
 * Lit les contextes stockés, en écartant les contextes expirés.
 *
 * Ne lève jamais : cette fonction est appelée depuis les gardes du routeur, où une
 * exception bloquerait toute navigation.
 */
function readStoredContexts (): StoredContexts | null {
  let rawStoredValue: string | null
  try {
    rawStoredValue = localStorage.getItem(STORAGE_KEY)
  } catch (error) {
    console.warn(`${LOG_PREFIX} lecture impossible`, error)
    return null
  }

  if (rawStoredValue === null) {
    return null
  }

  const parsedContexts = parseStoredContexts(rawStoredValue)
  if (parsedContexts === null) {
    clearAllAuthenticationRedirectContexts()
    return null
  }

  return purgeExpiredContexts(parsedContexts)
}

/**
 * Analyse le contenu stocké. Un contenu illisible ou d'un format inconnu est jeté
 * plutôt que deviné.
 */
function parseStoredContexts (rawStoredValue: string): StoredContexts | null {
  try {
    const parsedValue = JSON.parse(rawStoredValue)
    if (parsedValue === null || typeof parsedValue !== 'object') {
      return null
    }

    if (parsedValue.version !== STORAGE_FORMAT_VERSION) {
      return null
    }

    if (parsedValue.contextsByKey === null || typeof parsedValue.contextsByKey !== 'object') {
      return null
    }

    return parsedValue as StoredContexts
  } catch (error) {
    console.warn(`${LOG_PREFIX} contenu stocké illisible`, error)
    return null
  }
}

/**
 * Retire les contextes arrivés à expiration et persiste le résultat s'il a changé.
 */
function purgeExpiredContexts (storedContexts: StoredContexts): StoredContexts | null {
  const nowEpochMilliseconds = Date.now()
  const contextsByKey: Record<string, AuthenticationRedirectContext> = {}
  let hasRemovedContext = false

  Object.entries(storedContexts.contextsByKey).forEach(([contextKey, context]) => {
    if (context.expiresAtEpochMilliseconds > nowEpochMilliseconds) {
      contextsByKey[contextKey] = context
      return
    }
    hasRemovedContext = true
  })

  if (!hasRemovedContext) {
    return storedContexts
  }

  if (Object.keys(contextsByKey).length === 0) {
    clearAllAuthenticationRedirectContexts()
    return null
  }

  const activeContextKey = storedContexts.activeContextKey !== null &&
    contextsByKey[storedContexts.activeContextKey] !== undefined
    ? storedContexts.activeContextKey
    : null

  const purgedContexts: StoredContexts = {
    ...storedContexts,
    activeContextKey: activeContextKey,
    contextsByKey: contextsByKey
  }
  writeStoredContexts(purgedContexts)

  return purgedContexts
}

/**
 * Persiste les contextes. Un échec d'écriture est signalé, jamais silencieux.
 */
function writeStoredContexts (storedContexts: StoredContexts): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storedContexts))
  } catch (error) {
    console.warn(`${LOG_PREFIX} écriture impossible`, error)
  }
}

/**
 * Liste les clés de l'ancienne implémentation encore présentes.
 */
function collectLegacyOAuthKeys (): string[] {
  const legacyKeys: string[] = []
  for (let keyIndex = 0; keyIndex < localStorage.length; keyIndex += 1) {
    const currentKey = localStorage.key(keyIndex)
    if (currentKey !== null && currentKey.startsWith('oauth_')) {
      legacyKeys.push(currentKey)
    }
  }

  // `oauth_data` en dernier : en cas de contextes concurrents, le flux direct est le
  // plus récemment écrit par l'ancienne implémentation.
  return legacyKeys.sort((firstKey, secondKey) => {
    if (firstKey === 'oauth_data') {
      return 1
    }
    if (secondKey === 'oauth_data') {
      return -1
    }
    return firstKey.localeCompare(secondKey)
  })
}

/**
 * Convertit une entrée de l'ancienne implémentation dans le format courant.
 */
function parseLegacyOAuthEntry (legacyKey: string): AuthenticationRedirectContext | null {
  const rawLegacyValue = localStorage.getItem(legacyKey)
  if (rawLegacyValue === null) {
    return null
  }

  try {
    const parsedValue = JSON.parse(rawLegacyValue)
    if (parsedValue === null || typeof parsedValue !== 'object') {
      return null
    }

    const oauthAuthorizationRequest: OAuthAuthorizationRequest = {}
    AUTHORIZATION_REQUEST_PARAMETER_NAMES.forEach((parameterName) => {
      const parameterValue = parsedValue[parameterName]
      if (isNonEmptyString(parameterValue)) {
        oauthAuthorizationRequest[parameterName] = parameterValue
      }
    })

    if (Object.keys(oauthAuthorizationRequest).length === 0) {
      return null
    }

    return {
      ...buildEmptyContext(),
      oauthAuthorizationRequest: oauthAuthorizationRequest
    }
  } catch (error) {
    return null
  }
}

/**
 * Supprime les emplacements de l'ancienne implémentation, y compris l'instantané
 * global du magasin qui servait de persistance de secours.
 */
function removeLegacyStorageEntries (): void {
  collectLegacyOAuthKeys().forEach((legacyKey) => localStorage.removeItem(legacyKey))
  localStorage.removeItem('store')
}
